/**
 * Prompt Changes Sidebar — right-side Sheet displaying proposed prompt
 * changes with inline diffs and edit/delete capabilities.
 *
 * Opened via a trigger button in the review phase. Uses the existing
 * Sheet primitive and reuses ChangesAccordion for the change list.
 */

import React from 'react';
import { Pencil } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import ChangesAccordion from './ChangesAccordion';
import type { PromptChange } from '@/lib/api/versionWorkflow';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: PromptChange[];
  onSaveEdit: (changeId: string, newText: string) => void;
  onDelete: (changeId: string) => void;
  readOnly?: boolean;
}

const PromptChangesSidebar: React.FC<Props> = ({
  open,
  onOpenChange,
  changes,
  onSaveEdit,
  onDelete,
  readOnly,
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/40 shrink-0">
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-muted-foreground" />
            <SheetTitle className="text-base">
              Prompt Changes
              {changes.length > 0 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  · {changes.length} Section{changes.length !== 1 ? 's' : ''} Modified
                </span>
              )}
            </SheetTitle>
          </div>
          <SheetDescription>Review and edit proposed changes</SheetDescription>
        </SheetHeader>

        {/* Changes list */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {changes.length > 0 ? (
            <ChangesAccordion
              changes={changes}
              onSaveEdit={onSaveEdit}
              onDelete={onDelete}
              readOnly={readOnly}
            />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No changes to review.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PromptChangesSidebar;
